import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import SmartboardCanvas, { PageAnnotations, DrawToolbar, DrawToolState } from './SmartboardCanvas';
import VideoWithSpeed from './VideoWithSpeed';
import { studentApi, teacherApi, SectionQuestionV2, AnswerDetailV2 } from '../services/api';
import TestRunner from './TestRunner';
import { useAuth } from '../contexts/AuthContext';
import { displayBookSectionLabel, groupBookSections } from '../utils/bookSectionGrouping';
import { setFunctional } from '../utils/cookieConsent';

// PDF.js worker — served via CDN. Safe to pin to the bundled version.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type Modal = null | 'teste-basla' | 'teste-basla-uyari' | 'optik-basla' | 'sonuclar' | 'cevaplar' | 'soru-cozumu' | 'soru-cozumu-uyari' | 'pdf' | 'pdf-ca' | 'bookpdf' | 'ders-videosu';

interface ReviewAnswer {
  question_number: number;
  correct_option: string;
  selected_option: string | null;
  is_correct: boolean;
  // P3 new-model attempts also carry these (per-case review detail).
  question_id?: number | null;
  points_earned?: number | null;
  points_total?: number | null;
  detail?: AnswerDetailV2 | null;
}

interface LatestAttempt {
  id: string;
  score_correct: number;
  score_total: number;
  submitted_at: string;
  time_spent_seconds: number | null;
}

// Superset question shape — new-model books (P3) add typed fields on top of
// the legacy {question_number, image_url, video_url} trio.
type SectionQuestion = SectionQuestionV2;

interface Section {
  section_label: string;
  section_display_label?: string | null;
  section_category?: string;
  // Topic (alt başlık) the test sits under (digipo Ünite → Konu → test). When
  // present, the İçerik column groups by topic under a Ünite (category) header.
  section_topic?: string | null;
  // Per-lesson teaching video (digipo ders_videosu) — a "Ders Videosu" card.
  lesson_video_url?: string | null;
  // Node-attached lecture content (book_node_contents → content_items): videos,
  // PDFs, images, links authored in the İçerikler pool and attached to this
  // lesson's node. The converged home for all non-question media.
  contents?: Array<{ id: string; title: string; short_title?: string | null; type: string | null; url: string | null }>;
  pdf_url?: string | null;
  test_number: number;
  question_count: number;
  question_numbers: number[];
  max_options: number;
  questions: SectionQuestion[];
  topic_name?: string | null;
  // New-model identity (P3, content_model: 'v2' books)
  test_id?: number | null;
  node_id?: number | null;
  section_key?: string;
  // Printed page span of the alt başlık this test sits under, resolved server
  // -side (own span, else the bölüm's). Lets "PDF Üzerinde Çalış" open just this
  // lesson's pages instead of the whole book. Absent/null → whole book.
  printed_page_start?: number | null;
  printed_page_end?: number | null;
  points_total?: number;
  has_new_types?: boolean;
  // Present only after a successful attempt
  answers: ReviewAnswer[] | null;
  latest_attempt: LatestAttempt | null;
  // True when the student saved a draft but hasn't submitted — "started but
  // incomplete". Surfaced by /student/library/:bookId so the list can paint
  // these sections yellow (distinct from never-started).
  has_draft?: boolean;
}

// İçerik (node content_item) type → Turkish label for the card subtitle.
const CONTENT_TYPE_LABELS: Record<string, string> = {
  video: 'Video', audio: 'Ses', pdf: 'PDF', image: 'Görsel',
  video_link: 'Video', link_redirect: 'Bağlantı',
  lecture_note: 'Ders Notu', solution_video: 'Çözüm Videosu',
};
const contentTypeLabel = (t: string | null) => (t && CONTENT_TYPE_LABELS[t]) || 'İçerik';
// Video-ish contents open in the in-page player; everything else opens in a tab.
const isVideoContent = (t: string | null) => t === 'video' || t === 'video_link' || t === 'solution_video';

interface ContentItem {
  id: number;
  title: string;
  file_url: string;
  signedUrl?: string | null;
  content_type_id: number;
  is_answer_key?: boolean;
}

interface Book {
  id: number;
  title: string;
  display_name?: string;
  cover_url: string | null;
  // Whole-book catalog PDF (uploaded from the admin panel). When present the
  // "Kitap PDF" entry appears in the İçerik column. For digipo books this is
  // only the catalog SAMPLE — the full book is the per-page set below.
  pdf_url?: string | null;
  // digipo per-page PDFs: the real book content is one PDF per printed page.
  // has_page_pdfs drives the per-page reader; split_pdf_pages is the count.
  has_page_pdfs?: boolean;
  page_pdf_prefix?: string | null;
  split_pdf_pages?: number;
  // True only when the catalog/full-PDF scope has been proven to represent the
  // complete book. A verified catalog can serve whole-book mode as one file;
  // partial/sample catalogs must keep using the split-page set.
  pdf_scope_verified?: boolean;
  subject?: { name: string } | null;
  grade_levels?: { grade_number: number } | null;
  publishers?: { name: string } | null;
  content_items: ContentItem[];
  sections: Section[];
  book_format?: 'yf' | 'ca' | 'taktak' | 'edk' | 'standard';
  edk_description?: string | null;
}

// Whole catalogs can exceed 1,000 pages. Mounting every react-pdf <Page> (and
// every drawing canvas) at once freezes the browser even when the PDF itself is
// a single file. Keep lightweight placeholders and mount a page only when it is
// near the viewport. Once mounted it stays mounted so annotations and scroll
// position remain stable.
const LazyPdfPage: React.FC<{
  width: number;
  estimatedHeight: number;
  label: string;
  children: React.ReactNode;
}> = ({ width, estimatedHeight, label, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [nearViewport, setNearViewport] = useState(false);

  useEffect(() => {
    if (nearViewport) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setNearViewport(true);
        observer.disconnect();
      }
    }, { rootMargin: '1200px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [nearViewport]);

  return (
    <div ref={ref} style={{ width, minHeight: estimatedHeight }}>
      {nearViewport
        ? children
        : <div style={{ width, height: estimatedHeight }} className="flex items-center justify-center text-[12px] text-gray-400 bg-white">{label}</div>}
    </div>
  );
};

// Group sections by:
//  1. section_category if present (ÇK books: "Kazanım Denemeleri", "Tekrar Foyu", …)
//  2. topic_name from icerikler (EDK books)
//  3. section_label with trailing roman numerals stripped (fallback)
function groupByChapterStem(sections: Section[]): Array<{ key: string; stem: string; category: string; members: Section[] }> {
  return groupBookSections(sections);
}

// PdfLoader handles per-section annotation loading and renders all pages with
// per-page canvas overlays. Keeps a single toolbar state shared by all pages.
const PdfLoader: React.FC<{
  file: string;
  // digipo per-page mode: one presigned PDF per printed page. When provided,
  // each page is rendered as its own <Document> (the full book) instead of
  // loading `file` (which for digipo is only the catalog sample).
  pages?: { page: number; url: string | null }[];
  annKey: string;
  renderWidth: number;
  pdfScale: number;
  showCanvas: boolean;
  annotations: PageAnnotations;
  drawToolState: DrawToolState;
  onAnnotationsChange: (a: PageAnnotations) => void;
  onNumPages: (n: number) => void;
  onLoadAnnotations: (key: string) => void;
  currentAnnotationKey: string | null;
}> = ({ file, pages, annKey, renderWidth, showCanvas, annotations, drawToolState, onAnnotationsChange, onNumPages, onLoadAnnotations, currentAnnotationKey }) => {
  const [numPages, setNumPages] = useState(0);
  // Start with an A4-like estimate for the placeholder, then adopt the real
  // source page aspect as soon as the first nearby page loads. Board books are
  // often landscape; keeping the portrait estimate would leave a large blank
  // drawing area under every page and make a 1,099-page catalog unnecessarily
  // tall even though page rendering itself is lazy.
  const [pageAspect, setPageAspect] = useState(1.414);
  const renderHeight = Math.round(renderWidth * pageAspect);
  const rememberPageAspect = useCallback((page: any) => {
    const viewport = page?.getViewport?.({ scale: 1 });
    const next = Number(viewport?.height) / Number(viewport?.width);
    if (!Number.isFinite(next) || next <= 0) return;
    setPageAspect((current) => Math.abs(current - next) < 0.001 ? current : next);
  }, []);

  // Load annotations when section key changes
  useEffect(() => {
    if (annKey && annKey !== currentAnnotationKey) {
      onLoadAnnotations(annKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annKey]);

  // Report page count in per-page mode (single-file mode reports via
  // onLoadSuccess). Reset the parent while switching from a slice to a catalog;
  // otherwise the old slice count remains visible during the catalog load.
  useEffect(() => {
    if (pages) onNumPages(pages.length);
    else {
      setNumPages(0);
      onNumPages(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, file]);

  // Per-page overlay shared by both modes (annotations keyed by page number).
  const overlay = (pageNum: number) =>
    showCanvas && (
      <div className="absolute inset-0">
        <SmartboardCanvas
          page={pageNum}
          annotations={annotations}
          onAnnotationsChange={onAnnotationsChange}
          width={renderWidth}
          height={renderHeight}
          toolState={drawToolState}
        />
      </div>
    );

  // ── digipo per-page mode: one <Document> per printed page ────────────────
  if (pages) {
    return (
      <>
        {pages.map(({ page, url }) => (
          <div key={page} className="relative mb-2 shadow-md" style={{ width: renderWidth }}>
            <LazyPdfPage width={renderWidth} estimatedHeight={renderHeight} label={`Sayfa ${page}`}>
              {url ? (
                <Document file={url} loading={<div style={{ width: renderWidth, height: renderHeight }} className="bg-white/60" />} error={<div style={{ width: renderWidth, height: renderHeight }} className="flex items-center justify-center text-[12px] text-red-400">Sayfa {page} yüklenemedi</div>}>
                  <Page pageNumber={1} width={renderWidth} renderTextLayer={false} renderAnnotationLayer={false} onLoadSuccess={rememberPageAspect} />
                </Document>
              ) : (
                <div style={{ width: renderWidth, height: renderHeight }} className="flex items-center justify-center text-[12px] text-gray-400 bg-white">Sayfa {page} yok</div>
              )}
              {overlay(page)}
            </LazyPdfPage>
          </div>
        ))}
      </>
    );
  }

  // ── single-file mode (PLE whole-book / catalog / cevap anahtarı) ─────────
  return (
    <Document
      file={file}
      onLoadSuccess={({ numPages: n }) => { setNumPages(n); onNumPages(n); }}
      loading={<div className="py-20 text-[14px] text-gray-500">Yükleniyor...</div>}
      error={<div className="py-20 text-[14px] text-red-500">PDF yüklenemedi.</div>}
    >
      {numPages > 0 && Array.from({ length: numPages }, (_, i) => {
        const pageNum = i + 1;
          return (
            <div key={pageNum} className="relative mb-2 shadow-md" style={{ width: renderWidth }}>
              <LazyPdfPage width={renderWidth} estimatedHeight={renderHeight} label={`Sayfa ${pageNum}`}>
                <Page pageNumber={pageNum} width={renderWidth} renderTextLayer={false} renderAnnotationLayer={false} onLoadSuccess={rememberPageAspect} />
                {overlay(pageNum)}
              </LazyPdfPage>
            </div>
          );
      })}
    </Document>
  );
};

// Scrollable box that shows fade + chevron hints when there's more content
// above/below the fold — used for the Alıştırmalar dropdown so an expanded
// test's lower actions (e.g. "PDF Üzerinde Çalış") clearly read as reachable.
const ScrollFade: React.FC<{ maxHeight: number; className?: string; children: React.ReactNode }> = ({ maxHeight, className, children }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hint, setHint] = useState({ top: false, bottom: false });
  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content) return;
    const update = () => {
      const canScroll = el.scrollHeight > el.clientHeight + 2;
      setHint({
        top: canScroll && el.scrollTop > 4,
        bottom: canScroll && el.scrollTop + el.clientHeight < el.scrollHeight - 4,
      });
    };
    update();
    el.addEventListener('scroll', update);
    const ro = new ResizeObserver(update);
    ro.observe(content);
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, [children]);
  return (
    <div className="relative">
      <div ref={scrollRef} className={`overflow-y-auto ${className ?? ''}`} style={{ maxHeight }}>
        <div ref={contentRef}>{children}</div>
      </div>
      {hint.top && (
        <div className="pointer-events-none absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-[#fafafa] to-transparent" />
      )}
      {hint.bottom && (
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-9 bg-gradient-to-t from-[#fafafa] to-transparent flex items-end justify-center pb-1">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
        </div>
      )}
    </div>
  );
};

export interface BookDetailViewProps {
  // 'student' = full test-taking flow; 'teacher' = read-only (no Teste Başla,
  // answer key shown inline). Both render the identical three-column UI.
  mode: 'student' | 'teacher';
  bookId: number | string | undefined;
  autoOpenSection?: string;
  autoStartTest?: boolean;
  // Teacher deep-link: open the section's solution-video viewer immediately.
  autoOpenVideos?: boolean;
  // Teacher: back button in the header + custom exit target (library / an
  // assignment the teacher came from). Student leaves these unset and uses the
  // router navigate fallback.
  onExit?: () => void;
}

const BookDetailView: React.FC<BookDetailViewProps> = ({
  mode,
  bookId,
  autoOpenSection,
  autoStartTest,
  autoOpenVideos,
  onExit,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTeacher = mode === 'teacher';
  // API selector — the teacher reads the same-shaped payload from its own
  // read-only endpoints (see teacherApi.bookContent/bookPages/annotations).
  const fetchBookContent = useCallback(
    (id: string) => (isTeacher ? teacherApi.bookContent(id) : studentApi.bookContent(id)),
    [isTeacher],
  );
  const fetchBookPages = useCallback(
    (id: number | string, nodeId?: number | null) =>
      (isTeacher ? teacherApi.bookPages(id, nodeId) : studentApi.bookPages(id, nodeId)),
    [isTeacher],
  );
  const apiLoadAnnotations = useCallback(
    (id: number | string, key: string) => (isTeacher ? teacherApi.loadAnnotations(id, key) : studentApi.loadAnnotations(id, key)),
    [isTeacher],
  );
  const apiSaveAnnotations = useCallback(
    (id: number | string, key: string, data: any) => (isTeacher ? teacherApi.saveAnnotations(id, key, data) : studentApi.saveAnnotations(id, key, data)),
    [isTeacher],
  );
  const exitToLibrary = useCallback(() => {
    if (onExit) onExit();
    else navigate('/student/kutuphanem');
  }, [onExit, navigate]);
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedTopic, setSelectedTopic] = useState<number | null>(null);
  const [selectedExercise, setSelectedExercise] = useState<number | null>(null);
  // "Kitap PDF" selected in the İçerik (topic) column — shows a single
  // "PDF'i aç" button in the Alıştırma column instead of an exercise list.
  const [bookPdfMode, setBookPdfMode] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  // A node lecture-content video/link opened from the İçerik cards.
  const [contentVideo, setContentVideo] = useState<{ url: string; title: string } | null>(null);

  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [pdfWidth, setPdfWidth] = useState(900);
  const [pdfScale, setPdfScale] = useState(1.0);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  // digipo per-page reader: the page URLs, fetched when the reader opens.
  const [bookPages, setBookPages] = useState<{ page: number; url: string | null }[] | null>(null);
  // Which alt başlık's pages the reader should show. Set when "PDF Üzerinde
  // Çalış" is clicked inside a lesson; null means the whole book (the "Kitap
  // PDF" entry, or the lesson's "tüm kitabı aç" escape hatch). The backend
  // resolves the actual range from the node — this only carries WHICH node.
  const [pdfNode, setPdfNode] = useState<{ id: number; label: string; start?: number | null; end?: number | null } | null>(null);
  // Echoed back by the server so the header can state what is on screen even
  // when the range came from the bölüm rather than the lesson itself.
  const [pageSlice, setPageSlice] = useState<{ start: number | null; end: number | null; count: number } | null>(null);
  useEffect(() => {
    // A verified merged catalog is the efficient whole-book source. Split
    // pages remain authoritative for an alt başlık slice and as the fallback
    // when the available catalog is only a sample/partial PDF.
    const verifiedWholeCatalog = pdfNode == null && !!book?.pdf_scope_verified && !!book?.pdf_url;
    if (modal === 'bookpdf' && book?.has_page_pdfs && bookId != null && !verifiedWholeCatalog) {
      let cancelled = false;
      fetchBookPages(bookId, pdfNode?.id ?? null)
        .then((r) => {
          if (cancelled) return;
          setBookPages(r.data.pages ?? []);
          setPageSlice({
            start: r.data.page_start ?? null,
            end: r.data.page_end ?? null,
            count: r.data.count ?? 0,
          });
        })
        .catch(() => { if (!cancelled) { setBookPages([]); setPageSlice(null); } });
      return () => { cancelled = true; };
    }
    setBookPages(null);
    setPageSlice(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal, book?.has_page_pdfs, book?.pdf_scope_verified, book?.pdf_url, bookId, pdfNode?.id]);

  // Drawing state — shared across all per-page canvases via toolState prop
  const [drawToolState, setDrawToolState] = useState<DrawToolState>({ tool: 'pencil', color: '#2b2b2b', brushSize: 4 });
  const [annotations, setAnnotations] = useState<PageAnnotations>({});
  // Undo/redo history per PDF section key
  const [annHistory, setAnnHistory] = useState<PageAnnotations[]>([{}]);
  const [annHistoryIdx, setAnnHistoryIdx] = useState(0);
  // Track which PDF section's annotations are currently loaded
  const [currentAnnotationKey, setCurrentAnnotationKey] = useState<string | null>(null);

  // Sections where the student opened "Soru Çözüm Videoları" BEFORE completing
  // the test. Persisted so a refresh doesn't let them escape the rule.
  // When set, Teste Başla shows a disclaimer and the test is auto-scored empty.
  const watchedKey = bookId ? `videosPreviewed:${user?.id ?? 'anon'}:${bookId}` : null;
  const [videosPreviewed, setVideosPreviewed] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!watchedKey) return;
    try {
      const raw = localStorage.getItem(watchedKey);
      setVideosPreviewed(raw ? new Set(JSON.parse(raw)) : new Set());
    } catch { setVideosPreviewed(new Set()); }
  }, [watchedKey]);
  const markVideosPreviewed = (sectionLabel: string) => {
    if (!watchedKey) return;
    setVideosPreviewed((prev) => {
      if (prev.has(sectionLabel)) return prev;
      const next = new Set(prev);
      next.add(sectionLabel);
      setFunctional(watchedKey, JSON.stringify(Array.from(next)));
      return next;
    });
  };

  // Load annotations from server when the PDF section key changes
  const saveAnnotationsTimer = useRef<NodeJS.Timeout | null>(null);
  const loadAnnotationsForSection = useCallback((sectionKey: string) => {
    if (!bookId) return;
    setCurrentAnnotationKey(sectionKey);
    setAnnotations({});
    setAnnHistory([{}]);
    setAnnHistoryIdx(0);
    apiLoadAnnotations(bookId, sectionKey)
      .then((res) => {
        const data = res.data ?? {};
        if (Object.keys(data).length > 0) {
          setAnnotations(data);
          setAnnHistory([data]);
          setAnnHistoryIdx(0);
        }
      })
      .catch(() => {});
  }, [bookId, apiLoadAnnotations]);

  const handleAnnotationsChange = useCallback((updated: PageAnnotations) => {
    setAnnotations(updated);
    setAnnHistory((prev) => {
      const next = [...prev.slice(0, annHistoryIdx + 1), updated];
      return next.slice(-50); // keep last 50 states
    });
    setAnnHistoryIdx((i) => Math.min(i + 1, 49));
    // Debounced server save
    if (saveAnnotationsTimer.current) clearTimeout(saveAnnotationsTimer.current);
    if (bookId && currentAnnotationKey) {
      saveAnnotationsTimer.current = setTimeout(() => {
        apiSaveAnnotations(bookId, currentAnnotationKey, updated).catch(() => {});
      }, 1500);
    }
  }, [bookId, currentAnnotationKey, annHistoryIdx, apiSaveAnnotations]);

  const handleAnnotationUndo = useCallback(() => {
    const newIdx = Math.max(0, annHistoryIdx - 1);
    setAnnHistoryIdx(newIdx);
    setAnnotations(annHistory[newIdx] ?? {});
  }, [annHistory, annHistoryIdx]);

  const handleAnnotationRedo = useCallback(() => {
    const newIdx = Math.min(annHistory.length - 1, annHistoryIdx + 1);
    setAnnHistoryIdx(newIdx);
    setAnnotations(annHistory[newIdx] ?? {});
  }, [annHistory, annHistoryIdx]);

  const handleAnnotationClear = useCallback(() => {
    handleAnnotationsChange({});
  }, [handleAnnotationsChange]);

  useEffect(() => {
    if (!bookId) {
      setError('Kitap seçilmedi. Kütüphaneden bir kitap açın.');
      setLoading(false);
      return;
    }
    // Wipe per-book selection state before fetching the new book. Without
    // this, navigating from book A (on topic 2) to book B carries the
    // selectedTopic index forward — book B's Alıştırma column then paints
    // from its own 3rd group, which looks like "wrong data for the book"
    // even though the fetch itself is correct. Also reset draft/test state
    // so a partially-answered test from book A doesn't leak into book B.
    setSelectedUnite(null);
    setSelectedTopic(null);
    setSelectedExercise(null);
    setBookPdfMode(false);
    setModal(null);
    setRunningTest(null);
    setLoading(true);
    fetchBookContent(String(bookId))
      .then((res) => setBook(res.data as Book))
      .catch((e) => setError(e?.response?.data?.error ?? e.message ?? 'Kitap yüklenemedi'))
      .finally(() => setLoading(false));
  }, [bookId, fetchBookContent]);

  useEffect(() => {
    if ((modal !== 'pdf' && modal !== 'pdf-ca' && modal !== 'bookpdf') || !pdfContainerRef.current) return;
    const el = pdfContainerRef.current;
    const observer = new ResizeObserver((entries) => {
      const w = Math.round(entries[0].contentRect.width);
      if (w > 0) setPdfWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [modal]);

  const topicGroups = useMemo(() => groupByChapterStem(book?.sections ?? []), [book]);
  const currentTopic = selectedTopic !== null ? topicGroups[selectedTopic] : null;
  const currentExercises = currentTopic?.members ?? [];

  // İçerik column = Ünite (category) dropdowns → Konu (topic) items. Group the
  // topic groups by their Ünite, keeping each topic's index into topicGroups
  // (that index is what selectedTopic points at).
  const unites = useMemo(() => {
    type KonuEntry = { group: typeof topicGroups[number]; index: number };
    const m = new Map<string, KonuEntry[]>();
    topicGroups.forEach((g, index) => {
      const key = g.category || g.stem; // flat books (no category) → each topic is its own "Ünite"
      const arr = m.get(key) ?? [];
      arr.push({ group: g, index });
      m.set(key, arr);
    });
    return Array.from(m.entries());
  }, [topicGroups]);
  // Three-level navigation: Bölüm (col 1) → Alt Başlık (col 2) → İçerik (col 3).
  // selectedUnite is the Bölüm key into `unites`; selecting a Bölüm shows its
  // Alt Başlıks in the middle column, and picking one drives selectedTopic.
  const [selectedUnite, setSelectedUnite] = useState<string | null>(null);
  const currentUniteKonus = useMemo(
    () => (selectedUnite === null ? [] : unites.find(([k]) => k === selectedUnite)?.[1] ?? []),
    [unites, selectedUnite],
  );
  // Keep the Bölüm selection in sync with the selected topic — e.g. a deep-link
  // from Ödevlerim sets selectedTopic directly, so open its parent Bölüm too.
  useEffect(() => {
    if (!unites.length || selectedTopic === null) return;
    const u = unites.find(([, ks]) => ks.some(k => k.index === selectedTopic));
    if (u && u[0] !== selectedUnite) setSelectedUnite(u[0]);
  }, [unites, selectedTopic, selectedUnite]);
  const currentExercise = selectedExercise !== null ? currentExercises[selectedExercise] : null;

  // İçerik (node-attached content_items) is built per NODE on the backend →
  // identical across every exercise of a topic. Aggregate + dedupe once so it
  // renders as a single topic-level "İçerik" accordion under Alıştırma, not
  // repeated inside every exercise's detail column.
  const topicContents = useMemo(() => {
    const seen = new Set<string>();
    const out: NonNullable<Section['contents']> = [];
    for (const ex of currentTopic?.members ?? []) for (const ci of ex.contents ?? []) {
      if (!seen.has(ci.id)) { seen.add(ci.id); out.push(ci); }
    }
    return out;
  }, [currentTopic]);
  // Real exercises = sections backed by an actual test/lesson (test_id != null).
  // A content-only node (e.g. an exam book's solution-video subject) emits a
  // placeholder section with test_id null that carries ONLY İçerik — it must NOT
  // appear as an "Alıştırma". Keep the original members index for selection.
  const realExercises = useMemo(
    () => (currentTopic?.members ?? []).map((ex, i) => ({ ex, i })).filter(({ ex }) => ex.test_id != null),
    [currentTopic],
  );
  // A migrated alt başlık can be PDF-only. Keep one node-backed section as
  // the topic's PDF authority so the UI can open its verified page slice even
  // when there is no test row to expand.
  const topicPdfSection = useMemo(
    () => (currentTopic?.members ?? []).find((ex) =>
      ex.node_id != null && ex.printed_page_start != null && ex.printed_page_end != null),
    [currentTopic],
  );
  const [alistirmaOpen, setAlistirmaOpen] = useState(true);
  const [icerikOpen, setIcerikOpen] = useState(true);

  // Primary PDF: prefer non-answer-key PDF; fall back to first item
  const allPdfs = (book?.content_items ?? []).filter((c) => c.signedUrl);
  const pdfItem = allPdfs.find((c) => !c.is_answer_key) || allPdfs[0];
  const caItem = allPdfs.find((c) => c.is_answer_key) ?? null;

  const bookFormat = book?.book_format ?? 'standard';

  // Action panel for one Alıştırma (test) — rendered inline, directly beneath
  // the clicked test row in the İçerik column (a dropdown under THAT row, not
  // under the whole Alıştırmalar list).
  const renderExerciseActions = (ex: Section) => {
    const currentExercise = ex;
    const done = !!currentExercise.latest_attempt;
    const isYF = currentExercise.section_category === 'Yazılı Hazırlık Föyü';
    // Video-only lessons (digipo ders-videosu with no questions) show only the
    // Ders Videosu (+ PDF); no Teste Başla / Soru Çözüm on an empty test.
    const hasQuestions = currentExercise.question_count > 0;
    return (
      <div className="bg-[#fafafa] rounded-[16px] p-3 space-y-2 border border-gray-100">
        {/* Ders Videosu — the lesson's teaching video; ALWAYS on top. */}
        {currentExercise.lesson_video_url && (
          <div
            onClick={() => setModal('ders-videosu')}
            className="bg-white rounded-[14px] p-4 flex items-center gap-3 cursor-pointer border border-gray-100 hover:border-[#3973bf] transition-colors"
          >
            <div className="w-[28px] h-[28px] rounded-full bg-[#3973bf] flex-shrink-0 flex items-center justify-center">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="#fff"><path d="M2 1l8 5-8 5z"/></svg>
            </div>
            <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>Ders Videosu</p>
          </div>
        )}
        {/* Test — Teste Başla / results (non-YF sections with questions).
            Student-only: teachers can't solve, so the whole test block is hidden
            and the read-only "Soru Çözüm Videoları" below stands in. */}
        {!isTeacher && !isYF && hasQuestions && (done ? (
          <>
            <div
              onClick={() => setModal('sonuclar')}
              className="bg-white rounded-[14px] p-4 flex items-center gap-3 cursor-pointer border border-gray-100 hover:border-[#59e56e] transition-colors"
            >
              <div className="w-[28px] h-[28px] rounded-full bg-[#59e56e] flex-shrink-0" />
              <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>Sonucu Gör</p>
            </div>
            <div
              onClick={() => setModal('cevaplar')}
              className="bg-white rounded-[14px] p-4 flex items-center gap-3 cursor-pointer border border-gray-100 hover:border-[#3973bf] transition-colors"
            >
              <div className="w-[28px] h-[28px] rounded-full bg-[#3973bf] flex-shrink-0" />
              <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>Cevapları Gör</p>
            </div>
          </>
        ) : (
          <div
            onClick={() => setModal(videosPreviewed.has(currentExercise.section_label) ? 'teste-basla-uyari' : 'teste-basla')}
            className={`bg-white rounded-[14px] p-4 flex items-center gap-3 cursor-pointer border border-gray-100 transition-colors ${currentExercise.has_draft ? 'hover:border-[#f5c518]' : 'hover:border-[#3973bf]'}`}
          >
            <div className={`w-[28px] h-[28px] rounded-full flex-shrink-0 ${currentExercise.has_draft ? 'bg-[#f5c518]' : 'bg-[#3973bf]'}`} />
            <div className="flex-1">
              <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>{currentExercise.has_draft ? 'Teste Devam Et' : 'Teste Başla'}</p>
              {currentExercise.has_draft ? (
                <p className="text-[11px] font-medium mt-0.5" style={{ color: '#a97e00' }}>Kaldığın yerden devam edeceksin</p>
              ) : videosPreviewed.has(currentExercise.section_label) && (
                <p className="text-[11px] text-[#e5599b] font-medium mt-0.5">Videolar izlendi — boş kaydedilecek</p>
              )}
            </div>
          </div>
        ))}
        {/* Optiği Aç — solve from the printed book, mark A–E digitally. Same
            attempt/scoring as Teste Başla, but loads no question images. */}
        {!isTeacher && !isYF && hasQuestions && !done && (
          <div
            onClick={() => setModal('optik-basla')}
            className="bg-white rounded-[14px] p-4 flex items-center gap-3 cursor-pointer border border-gray-100 hover:border-[#e5599b] transition-colors"
          >
            <div className="w-[28px] h-[28px] rounded-full bg-[#e5599b]/15 flex-shrink-0 flex items-center justify-center">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e5599b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="3" width="16" height="18" rx="2" /><circle cx="9" cy="9" r="1.4" fill="#e5599b" stroke="none" /><line x1="13" y1="9" x2="17" y2="9" /><line x1="8" y1="14" x2="17" y2="14" />
              </svg>
            </div>
            <div className="flex-1">
              <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>Optiği Aç</p>
              <p className="text-[11px] text-[#888] font-medium mt-0.5">Kitaptan çöz, dijital işaretle</p>
            </div>
          </div>
        )}
        {/* Soru Çözüm Videoları — student: hidden once solved (Cevapları Gör
            covers it). Teacher: always available; viewer shows the answer key. */}
        {hasQuestions && (isTeacher || !currentExercise.latest_attempt) && (
          <div
            onClick={() => {
              if (!isTeacher && !isYF && !videosPreviewed.has(currentExercise.section_label)) {
                setModal('soru-cozumu-uyari');
              } else {
                setModal('soru-cozumu');
              }
            }}
            className="bg-white rounded-[14px] p-4 flex items-center gap-3 cursor-pointer border border-gray-100 hover:border-[#e5599b] transition-colors"
          >
            <div className="w-[28px] h-[28px] rounded-full bg-[#e5599b] flex-shrink-0" />
            <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>Soru Çözüm Videoları</p>
          </div>
        )}
        {/* PDF for this Alıştırma — only this lesson's printed pages when we
            know them, so the reader stops pulling the whole book (129 pages on
            average) to show a 4-5 page lesson. */}
        {((book?.has_page_pdfs
          && currentExercise.printed_page_start != null
          && currentExercise.printed_page_end != null)
          || currentExercise.pdf_url
          || pdfItem?.signedUrl) && (
          <div
            onClick={() => {
              if (book?.has_page_pdfs) {
                setPdfNode(
                  currentExercise.node_id != null
                    ? {
                        id: currentExercise.node_id,
                        label: currentExercise.section_topic || displayBookSectionLabel(currentExercise),
                        start: currentExercise.printed_page_start ?? null,
                        end: currentExercise.printed_page_end ?? null,
                      }
                    : null,
                );
                setModal('bookpdf');
              } else {
                setModal('pdf');
              }
            }}
            className="bg-white rounded-[14px] p-4 flex items-center gap-3 cursor-pointer border border-gray-100 hover:border-[#ffda09] transition-colors"
          >
            <div className="w-[28px] h-[28px] rounded-full border-2 border-[#ffda09] flex-shrink-0" />
            <div className="flex-1">
              <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>PDF Üzerinde Çalış</p>
              {book?.has_page_pdfs && currentExercise.printed_page_start != null && (
                <p className="text-[11px] text-[#888] font-medium mt-0.5">
                  Sayfa {currentExercise.printed_page_start}
                  {currentExercise.printed_page_end != null && currentExercise.printed_page_end !== currentExercise.printed_page_start
                    ? `-${currentExercise.printed_page_end}`
                    : ''}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ===== Test-taking state =====
  // P3: the actual test-taking UI moved into the unified <TestRunner>
  // component (4 soru tipi + legacy). This page only decides WHICH section
  // runs and refreshes itself when the runner closes. testCurrentQ survives
  // for the cevaplar / soru-cozumu review modals.
  const [runningTest, setRunningTest] = useState<Section | null>(null);
  const [runnerMode, setRunnerMode] = useState<'question' | 'optik'>('question');
  const [testCurrentQ, setTestCurrentQ] = useState(1);

  // Reset question cursor when entering review/solution modals so it always
  // starts from the first question of the currently-selected exercise.
  // Also: opening Soru Çözümü on a section without a completed attempt counts
  // as "pre-watching" — their eventual test submission gets auto-scored empty.
  useEffect(() => {
    if ((modal === 'cevaplar' || modal === 'soru-cozumu') && currentExercise) {
      setTestCurrentQ(currentExercise.question_numbers[0] ?? 1);
    }
    if (!isTeacher && modal === 'soru-cozumu' && currentExercise && !currentExercise.latest_attempt) {
      markVideosPreviewed(currentExercise.section_label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal, currentExercise]);

  // Klavye ile soru gezinme (#23) — ArrowLeft / ArrowRight tuşları test,
  // cevaplar ve soru çözümü ekranlarında prev/next butonlarıyla aynı işi
  // yapıyor. Input/textarea/contenteditable üzerinde fokus varsa devre dışı.
  useEffect(() => {
    const navModals = ['cevaplar', 'soru-cozumu'] as const;
    if (!modal || !navModals.includes(modal as any) || !currentExercise) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
      const nums = currentExercise.question_numbers;
      const idx = nums.indexOf(testCurrentQ);
      if (e.key === 'ArrowLeft' && idx > 0) setTestCurrentQ(nums[idx - 1]);
      else if (e.key === 'ArrowRight' && idx >= 0 && idx < nums.length - 1) setTestCurrentQ(nums[idx + 1]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modal, currentExercise, testCurrentQ]);

  // P3: launch the unified runner. Draft load/save, timer, submission (legacy
  // letters OR the v2 payload for new-model tests) all live inside TestRunner.
  const beginTest = (ex: Section, mode: 'question' | 'optik' = 'question') => {
    setSelectedExercise(currentExercises.indexOf(ex));
    setModal(null);
    setRunnerMode(mode);
    setRunningTest(ex);
  };

  // Silent re-fetch after the runner closes so section states (latest_attempt,
  // answers, has_draft) reflect what just happened.
  const refreshBook = useCallback(() => {
    if (!bookId) return;
    fetchBookContent(String(bookId))
      .then((res) => setBook(res.data as Book))
      .catch(() => { /* keep the stale view rather than blanking the page */ });
  }, [bookId, fetchBookContent]);

  // Deep-link support: coming from Ödevlerim's "Süreyi Başlat" lands us on
  // this page with autoOpenSection set. Once the book loads, select the
  // matching topic/exercise and — if the section has no completed attempt
  // yet — auto-start the test so the student lands straight in the timer.
  const autoOpenHandled = useRef(false);
  useEffect(() => {
    if (!book || !autoOpenSection || autoOpenHandled.current) return;
    for (let ti = 0; ti < topicGroups.length; ti++) {
      const members = topicGroups[ti].members;
      const ei = members.findIndex((m) => m.section_label === autoOpenSection);
      if (ei >= 0) {
        setSelectedTopic(ti);
        setSelectedExercise(ei);
        autoOpenHandled.current = true;
        if (isTeacher) {
          // Teacher deep-link (assignment "Soruları Görüntüle") lands straight
          // on the read-only solution-video viewer for the target section.
          if (autoOpenVideos && members[ei].question_count > 0) setModal('soru-cozumu');
        } else if (autoStartTest && !members[ei].latest_attempt) {
          setRunningTest(members[ei]);
        }
        break;
      }
    }
  }, [book, autoOpenSection, autoStartTest, autoOpenVideos, isTeacher, topicGroups]);

  const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E'];

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (error || !book) {
    return (
      <div className="py-20 text-center">
        <p className="text-gray-500 mb-4">{error ?? 'Kitap bulunamadı.'}</p>
        <button onClick={exitToLibrary} className="bg-[#3973bf] text-white px-5 py-2 rounded-[12px]">Kütüphaneye Dön</button>
      </div>
    );
  }

  // Header totals describe the hierarchy the student is actually navigating:
  // top-level Bölüm groups and real attached tests. A PDF-only leaf is a valid
  // alt başlık/material, but it must not inflate the Test count.
  const totalExercises = topicGroups.reduce(
    (sum, g) => sum + g.members.filter((member) => member.test_id != null).length,
    0,
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        {onExit && (
          <button
            onClick={exitToLibrary}
            aria-label="Geri"
            className="hover:bg-gray-100 rounded-full p-1 transition-colors"
          >
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round"><path d="M20 8l-8 8 8 8" /></svg>
          </button>
        )}
        <h1 style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Kitap İçeriği</h1>
      </div>

      {/* Book info bar */}
      <div className="flex gap-4 mb-5">
        <div className="bg-[#fafafa] rounded-[24px] p-4 flex gap-4 flex-1 border border-gray-100">
          <div className="w-[130px] h-[170px] rounded-[12px] overflow-hidden flex-shrink-0 bg-gray-100">
            <img src={book.cover_url || '/a-book1.png'} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-1">
              <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 500, color: '#2b2b2b' }}>{book.display_name ?? book.title}</p>
              {bookFormat === 'edk' && <span className="text-[11px] font-bold text-white bg-[#3973bf] rounded-[6px] px-2 py-0.5">EDK</span>}
              {bookFormat === 'yf' && <span className="text-[11px] font-bold text-white bg-[#e5599b] rounded-[6px] px-2 py-0.5">Yazılı Foy</span>}
              {bookFormat === 'taktak' && <span className="text-[11px] font-bold text-white bg-[#6d79ff] rounded-[6px] px-2 py-0.5">Boşluk Doldurma</span>}
            </div>
            {book.subject?.name && <p className="text-[14px] text-[#3973bf] font-medium mb-1">{book.subject.name}</p>}
            {book.grade_levels?.grade_number && <p className="text-[13px] text-gray-500 mb-2">{book.grade_levels.grade_number}. Sınıf</p>}
            {book.edk_description && <p className="text-[12px] text-gray-500 mb-2 leading-relaxed">{book.edk_description}</p>}
            {bookFormat === 'yf' && <p className="text-[12px] text-[#e5599b] mb-2">Bu kitap yazılı foy formatındadır. Çoktan seçmeli soru içermemektedir.</p>}
            {bookFormat === 'taktak' && <p className="text-[12px] text-[#6d79ff] mb-2">Bu kitap boşluk doldurma soruları içermektedir. Çoktan seçmeli soru bulunmamaktadır.</p>}
            <div className="flex justify-between text-[14px] font-medium text-[#2b2b2b] mb-1">
              <span>{unites.length} Bölüm</span><span>{totalExercises} Test</span>
            </div>
            <p className="text-[12px] text-gray-500 mt-1">Yayınevi: {book.publishers?.name ?? '—'}</p>
          </div>
        </div>

        <div className="bg-[#fafafa] rounded-[24px] p-4 w-[320px] xl:w-[400px] flex items-center justify-center border border-gray-100 flex-shrink-0">
          <p className="text-[14px] text-[#2b2b2b] text-center">Bu Kitaba Atanmış Ödeviniz Bulunmamaktadır</p>
        </div>
      </div>

      {topicGroups.length === 0 && !book.pdf_url ? (
        <div className="bg-[#fafafa] rounded-[16px] p-8 text-center border border-gray-100">
          <p className="text-[15px] text-gray-500">Bu kitap için henüz test/alıştırma eklenmemiş.</p>
        </div>
      ) : (
        <>
          {/* Column headers. Same flex+gap as the content row below so the columns
              line up; the Alıştırma title gets pl-3 to sit above its card (the
              Alıştırma column wraps its cards in a padded container). The third
              column title is intentionally blank. */}
          <div className="flex gap-3 mb-3">
            <div className="w-[30%]"><span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>Bölümler</span></div>
            <div className="w-[35%] pl-3"><span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>Alt Başlıklar</span></div>
            <div className="w-[35%] pl-3"><span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>Materyaller</span></div>
          </div>

          <div className="flex gap-3">
            {/* COL 1 — Bölüm (Ünite): flat & clickable, no dropdown. Selecting a
                Bölüm shows its Alt Başlıks in the middle "Materyaller" column. */}
            <div className="w-[30%] space-y-2">
              {unites.map(([unite]) => {
                const active = selectedUnite === unite && !bookPdfMode;
                return (
                  <button
                    key={unite}
                    onClick={() => { setSelectedUnite(unite); setSelectedTopic(null); setSelectedExercise(null); setBookPdfMode(false); }}
                    className={`w-full text-left bg-[#fafafa] rounded-[16px] p-4 flex items-center justify-between gap-2 border transition-colors ${
                      active ? 'border-[#3973bf]' : 'border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <span style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 700, color: '#2b2b2b' }}>{unite}</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2b2b2b" strokeWidth="2" className="flex-shrink-0"><path d="M6 3l5 5-5 5"/></svg>
                  </button>
                );
              })}
              {book.pdf_url && (
                <div
                  onClick={() => { setBookPdfMode(true); setSelectedUnite(null); setSelectedTopic(null); setSelectedExercise(null); }}
                  className={`bg-[#fafafa] rounded-[16px] p-4 flex items-center justify-between cursor-pointer border transition-colors ${
                    bookPdfMode ? 'border-[#3973bf]' : 'border-gray-100 hover:border-gray-200'
                  }`}
                >
                  <div>
                    <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>Kitap PDF</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 400, color: '#888' }}>Kitabın tam PDF'i</p>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2b2b2b" strokeWidth="2"><path d="M6 3l5 5-5 5"/></svg>
                </div>
              )}
            </div>

            {/* COL 2 — Materyaller: the selected Bölüm's Alt Başlıks, flat &
                clickable. Picking one drives the İçerik column on the right. */}
            <div className="w-[35%]">
              {bookPdfMode ? (
                <div className="bg-[#fafafa] rounded-[16px] p-3 border border-gray-100">
                  <div
                    // "Kitap PDF" is the whole book by definition — clear any
                    // lesson range left over from a previous open.
                    onClick={() => { setPdfNode(null); setModal('bookpdf'); }}
                    className="bg-white rounded-[14px] p-4 flex items-center gap-3 cursor-pointer border border-gray-100 hover:border-[#ffda09] transition-colors"
                  >
                    <div className="w-[28px] h-[28px] rounded-full border-2 border-[#ffda09] flex-shrink-0" />
                    <p style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>PDF'i aç</p>
                  </div>
                </div>
              ) : selectedUnite !== null && currentUniteKonus.length > 0 ? (
                <div className="bg-[#fafafa] rounded-[16px] p-3 border border-gray-100 space-y-1.5 max-h-[560px] overflow-y-auto">
                  {currentUniteKonus.map(({ group, index }) => (
                    <button
                      key={group.key}
                      onClick={() => { setSelectedTopic(index); setSelectedExercise(null); }}
                      className={`w-full text-left bg-white rounded-[14px] p-4 flex items-center justify-between gap-2 border transition-colors ${
                        selectedTopic === index ? 'border-[#3973bf]' : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <span style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>{group.stem}</span>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2b2b2b" strokeWidth="2" className="flex-shrink-0"><path d="M6 3l5 5-5 5"/></svg>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="bg-[#fafafa] rounded-[16px] p-6 border border-gray-100 flex items-center justify-center min-h-[200px]">
                  <p className="text-[14px] text-gray-400 text-center">Bir bölüm seçin</p>
                </div>
              )}
            </div>

            {/* COL 3 — İçerik: the selected Alt Başlık's tests (Alıştırmalar);
                click a test to reveal its actions. Plus node-attached İçerik. */}
            <div className="w-[35%]">
              {bookPdfMode ? null : selectedTopic === null ? (
                <div className="bg-[#fafafa] rounded-[16px] p-6 border border-gray-100 flex items-center justify-center min-h-[200px]">
                  <p className="text-[14px] text-gray-400 text-center">Bir alt başlık seçin</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Alıştırmalar — tests under this Alt Başlık; click to reveal actions. */}
                  {realExercises.length > 0 && (
                    <div className="bg-[#fafafa] rounded-[16px] border border-gray-100 overflow-hidden">
                      <button
                        onClick={() => setAlistirmaOpen(o => !o)}
                        className="w-full flex items-center justify-between p-4 text-left"
                      >
                        <span style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 700, color: '#2b2b2b' }}>Alıştırmalar</span>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2b2b2b" strokeWidth="2" style={{ transform: alistirmaOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}><path d="M6 3l5 5-5 5"/></svg>
                      </button>
                      {alistirmaOpen && (
                        <ScrollFade maxHeight={440} className="px-2 pb-2">
                          <div className="space-y-1.5">
                          {realExercises.map(({ ex, i }) => {
                            const open = selectedExercise === i;
                            return (
                              <React.Fragment key={ex.test_id ?? `${ex.section_label}:${i}`}>
                                <div
                                  onClick={() => setSelectedExercise(open ? null : i)}
                                  className={`bg-white rounded-[14px] p-4 flex items-center gap-2 cursor-pointer border transition-colors ${
                                    open ? 'border-[#3973bf]' : 'border-gray-100 hover:border-gray-200'
                                  }`}
                                >
                                  <p className="flex-1 min-w-0 truncate" style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>{displayBookSectionLabel(ex)}</p>
                                  {ex.latest_attempt
                                    ? <span className="w-2 h-2 rounded-full bg-[#59e56e] flex-shrink-0" title="Çözüldü" />
                                    : ex.has_draft
                                      ? <span className="w-2 h-2 rounded-full bg-[#f5c518] flex-shrink-0" title="Devam ediyor" />
                                      : null}
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2b2b2b" strokeWidth="2" className="flex-shrink-0" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}><path d="M6 3l5 5-5 5"/></svg>
                                </div>
                                {/* Actions dropdown — right below THIS test row. */}
                                {open && (
                                  <div className="mt-1.5">
                                    {renderExerciseActions(ex)}
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                          </div>
                        </ScrollFade>
                      )}
                    </div>
                  )}

                  {/* Alt başlık PDF — opens only this topic's verified page
                      range, including PDF-only topics with no test/content. */}
                  {book?.has_page_pdfs && topicPdfSection && (
                    <div
                      onClick={() => {
                        setPdfNode({
                          id: topicPdfSection.node_id!,
                          label: topicPdfSection.section_topic || topicPdfSection.section_label,
                          start: topicPdfSection.printed_page_start ?? null,
                          end: topicPdfSection.printed_page_end ?? null,
                        });
                        setModal('bookpdf');
                      }}
                      className="bg-white rounded-[14px] p-4 flex items-center gap-3 cursor-pointer border border-gray-100 hover:border-[#ffda09] transition-colors"
                    >
                      <div className="w-[28px] h-[28px] rounded-full border-2 border-[#ffda09] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="truncate" style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>Alt Başlık PDF</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          Sayfa {topicPdfSection.printed_page_start}-{topicPdfSection.printed_page_end}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* İçerik — node-attached content_items (book_node_contents),
                      topic-level, so shown once for the whole Alt Başlık. */}
                  {topicContents.length > 0 && (
                    <div className="bg-[#fafafa] rounded-[16px] border border-gray-100 overflow-hidden">
                      <button
                        onClick={() => setIcerikOpen(o => !o)}
                        className="w-full flex items-center justify-between p-4 text-left"
                      >
                        <span style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 700, color: '#2b2b2b' }}>İçerik</span>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2b2b2b" strokeWidth="2" style={{ transform: icerikOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}><path d="M6 3l5 5-5 5"/></svg>
                      </button>
                      {icerikOpen && (
                        <div className="px-2 pb-2 space-y-1.5">
                          {topicContents.map((ci) => (
                            <div
                              key={ci.id}
                              onClick={() => { if (!ci.url) return; if (isVideoContent(ci.type)) setContentVideo({ url: ci.url, title: ci.short_title || ci.title }); else window.open(ci.url, '_blank', 'noopener'); }}
                              className="bg-white rounded-[14px] p-4 flex items-center gap-3 cursor-pointer border border-gray-100 hover:border-[#7c3aed] transition-colors"
                            >
                              <div className="w-[28px] h-[28px] rounded-full bg-[#7c3aed] flex-shrink-0 flex items-center justify-center">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="#fff"><path d="M2 1l8 5-8 5z" /></svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="truncate" style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>{ci.short_title || ci.title}</p>
                                <p className="text-[11px] text-gray-400 mt-0.5">{contentTypeLabel(ci.type)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {realExercises.length === 0 && topicContents.length === 0 && !topicPdfSection && (
                    <div className="bg-[#fafafa] rounded-[16px] p-6 border border-gray-100 flex items-center justify-center min-h-[160px]">
                      <p className="text-[14px] text-gray-400 text-center">Bu konu için içerik yok.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ===== MODALS ===== */}

      {/* Soru Çözümü Uyarı — pops up BEFORE opening the videos when the test
          hasn't been taken yet. Student must acknowledge the 0-score consequence. */}
      {modal === 'soru-cozumu-uyari' && currentExercise && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setModal(null)} />
          <div className="relative bg-white rounded-[20px] w-full max-w-[600px] p-6 border-4 border-[#e5599b]">
            <button onClick={() => setModal(null)} className="absolute top-4 right-4 text-[#2b2b2b] text-2xl">×</button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-[48px] h-[48px] rounded-full bg-[#e5599b] flex items-center justify-center text-white text-[24px] font-bold flex-shrink-0">!</div>
              <h2 className="text-[22px] font-bold text-[#e5599b]">Dur, Testi Henüz Çözmedin</h2>
            </div>
            <p className="text-[14px] text-[#2b2b2b] mb-3">
              <strong>{displayBookSectionLabel(currentExercise)}</strong> bölümünün soru çözüm videolarını izlemek üzeresin.
            </p>
            <p className="text-[14px] text-[#2b2b2b] mb-3 font-semibold">
              Bu videoları önce izlersen, testi sonradan çözsen bile cevapların <strong>boş</strong> kabul edilecek ve performansına <strong>0 puan</strong> olarak yansıyacaktır.
            </p>
            <p className="text-[13px] text-gray-500 mb-5">
              Önerimiz: önce testi çöz, sonra hatalarını videolardan incele. Öğrenmenin yolu denemekten geçer.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setModal('teste-basla'); }}
                className="bg-[#3973bf] text-white text-[14px] font-semibold px-6 py-3 rounded-[12px]"
              >Önce Testi Çöz</button>
              <button onClick={() => setModal(null)} className="bg-gray-200 text-[#2b2b2b] text-[14px] font-semibold px-6 py-3 rounded-[12px]">Vazgeç</button>
              <button
                onClick={() => { markVideosPreviewed(currentExercise.section_label); setModal('soru-cozumu'); }}
                className="bg-[#e5599b] text-white text-[14px] font-semibold px-6 py-3 rounded-[12px]"
              >Yine de İzle (test boş sayılacak)</button>
            </div>
          </div>
        </div>
      )}

      {/* Teste Başla Uyarı — shown ONLY when student already watched the solution
          videos for this section. Confirms their test will be auto-scored empty. */}
      {modal === 'teste-basla-uyari' && currentExercise && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-white rounded-[20px] w-full max-w-[600px] p-6 border-4 border-[#e5599b]">
            <button onClick={() => setModal(null)} className="absolute top-4 right-4 text-[#2b2b2b] text-2xl">×</button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-[48px] h-[48px] rounded-full bg-[#e5599b] flex items-center justify-center text-white text-[24px] font-bold">!</div>
              <h2 className="text-[22px] font-bold text-[#e5599b]">Dikkat — Video İzlendi</h2>
            </div>
            <p className="text-[14px] text-[#2b2b2b] mb-3">
              Bu bölümün (<strong>{displayBookSectionLabel(currentExercise)}</strong>) soru çözüm videolarını test çözmeden önce izledin.
            </p>
            <p className="text-[14px] text-[#2b2b2b] mb-3 font-semibold">
              Kurala göre, videoları önceden izlediğin için test cevapların <strong>boş</strong> kabul edilecek ve performansına <strong>0 puan</strong> olarak yansıyacaktır.
            </p>
            <p className="text-[13px] text-gray-500 mb-5">
              İstersen yine de çöz — pratik olsun. Seçtiklerin saklanmayacak, sadece boş bir deneme olarak kaydedilecek.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setModal(null)} className="bg-gray-200 text-[#2b2b2b] text-[14px] font-semibold px-6 py-3 rounded-[12px]">Vazgeç</button>
              <button onClick={() => beginTest(currentExercise)} className="bg-[#e5599b] text-white text-[14px] font-semibold px-6 py-3 rounded-[12px]">Yine de Başla</button>
            </div>
          </div>
        </div>
      )}

      {/* Teste Başla — confirmation before starting the test */}
      {modal === 'teste-basla' && currentExercise && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[700px] p-6">
            <button onClick={() => setModal(null)} className="absolute top-4 right-4 text-[#2b2b2b] text-2xl">×</button>
            <h2 style={{ fontFamily: 'Inter', fontSize: 28, fontWeight: 700, color: '#3973bf', marginBottom: 16 }}>Teste Başla</h2>
            <div className="bg-white rounded-[16px] p-4 border border-gray-100 mb-4">
              <p className="text-[14px] font-bold text-[#2b2b2b]">Test</p>
              <p className="text-[14px] text-[#2b2b2b]">{currentTopic?.stem} — {displayBookSectionLabel(currentExercise)}</p>
            </div>
            <div className="flex gap-4 mb-6">
              <div className="flex-1 bg-white rounded-[16px] p-4 border-2 border-[#3973bf] text-center">
                <p className="text-[14px] font-semibold text-[#3973bf]">Toplam Puan</p>
                <p className="text-[28px] font-bold text-[#3973bf]">{currentExercise.question_count}</p>
              </div>
              <div className="flex-1 bg-white rounded-[16px] p-4 border-2 border-[#e5599b] text-center">
                <p className="text-[14px] font-semibold text-[#e5599b]">Soru Sayısı</p>
                <p className="text-[28px] font-bold text-[#e5599b]">{currentExercise.question_count}</p>
              </div>
            </div>
            <p className="text-[13px] text-[#2b2b2b] mb-1">Soruları PDF'te okuyup, bu test ekranında işaretleyeceksin. Geçirdiğin süre ve skorun performans tablonuza kaydedilecek.</p>
            <p className="text-[13px] text-[#2b2b2b] mb-6">Hazırsan testi başlat.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setModal(null)} className="bg-gray-200 text-[#2b2b2b] text-[14px] font-semibold px-6 py-3 rounded-[12px]">Vazgeç</button>
              <button onClick={() => beginTest(currentExercise)} className="bg-[#59e56e] text-white text-[14px] font-semibold px-6 py-3 rounded-[12px]">Süreyi Başlat</button>
            </div>
          </div>
        </div>
      )}

      {/* Optiği Aç — start the answer-sheet flow (no images, mark A–E only) */}
      {modal === 'optik-basla' && currentExercise && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[700px] p-6">
            <button onClick={() => setModal(null)} className="absolute top-4 right-4 text-[#2b2b2b] text-2xl">×</button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-[44px] h-[44px] rounded-[12px] bg-[#e5599b]/15 flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#e5599b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="3" width="16" height="18" rx="2" /><circle cx="9" cy="9" r="1.5" fill="#e5599b" stroke="none" /><line x1="13" y1="9" x2="17" y2="9" /><circle cx="9" cy="14" r="1.5" /><line x1="13" y1="14" x2="17" y2="14" />
                </svg>
              </div>
              <h2 style={{ fontFamily: 'Inter', fontSize: 26, fontWeight: 700, color: '#e5599b' }}>Optiği Aç</h2>
            </div>
            <div className="bg-white rounded-[16px] p-4 border border-gray-100 mb-4">
              <p className="text-[14px] font-bold text-[#2b2b2b]">Test</p>
              <p className="text-[14px] text-[#2b2b2b]">{currentTopic?.stem} — {displayBookSectionLabel(currentExercise)}</p>
            </div>
            <div className="flex gap-4 mb-6">
              <div className="flex-1 bg-white rounded-[16px] p-4 border-2 border-[#e5599b] text-center">
                <p className="text-[14px] font-semibold text-[#e5599b]">Soru Sayısı</p>
                <p className="text-[28px] font-bold text-[#e5599b]">{currentExercise.question_count}</p>
              </div>
              <div className="flex-1 bg-white rounded-[16px] p-4 border-2 border-[#3973bf] text-center">
                <p className="text-[14px] font-semibold text-[#3973bf]">Şık Sayısı</p>
                <p className="text-[28px] font-bold text-[#3973bf]">{currentExercise.max_options}</p>
              </div>
            </div>
            <p className="text-[13px] text-[#2b2b2b] mb-1">Soruları <strong>kitabından</strong> çözeceksin — bu ekranda sadece cevaplarını (A–E) işaretlersin, soru görselleri yüklenmez. Süren ve skorun performans tablonuza kaydedilir.</p>
            {videosPreviewed.has(currentExercise.section_label) && (
              <p className="text-[13px] text-[#e5599b] font-semibold mb-1">Bu bölümün çözüm videolarını önceden izledin — işaretlerin boş kabul edilecek.</p>
            )}
            <p className="text-[13px] text-[#2b2b2b] mb-6">Hazırsan optiği aç.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setModal(null)} className="bg-gray-200 text-[#2b2b2b] text-[14px] font-semibold px-6 py-3 rounded-[12px]">Vazgeç</button>
              <button onClick={() => beginTest(currentExercise, 'optik')} className="bg-[#e5599b] text-white text-[14px] font-semibold px-6 py-3 rounded-[12px]">Optiği Aç</button>
            </div>
          </div>
        </div>
      )}

      {/* P3: unified test runner — handles legacy letters AND the 4 new
          question types, drafts (server-side), timer, submit (v2 payload for
          new-model tests) and its own summary/review screens. */}
      {runningTest && book && (
        <TestRunner
          bookId={book.id}
          sectionLabel={runningTest.section_label}
          testId={runningTest.test_id ?? null}
          section={runningTest as any}
          serverDraft
          mode={runnerMode}
          blankOnSubmit={videosPreviewed.has(runningTest.section_label)}
          onClose={() => { setRunningTest(null); refreshBook(); }}
        />
      )}


      {/* Sonuçlar — score summary after test submission */}
      {modal === 'sonuclar' && currentExercise?.latest_attempt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setModal(null)} />
          <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[700px] p-6">
            <button onClick={() => setModal(null)} className="absolute top-4 right-4 text-[#2b2b2b] text-2xl">×</button>
            <h2 style={{ fontFamily: 'Inter', fontSize: 28, fontWeight: 700, color: '#3973bf', marginBottom: 20 }}>Sonuçlar</h2>
            <div className="flex gap-4 mb-6">
              <div className="flex-1 space-y-3">
                <div className="bg-white rounded-[12px] p-3 border border-gray-100"><p className="text-[12px] text-gray-500">Kitap</p><p className="text-[14px] font-medium text-[#2b2b2b]">{book?.display_name ?? book?.title}</p></div>
                <div className="bg-white rounded-[12px] p-3 border border-gray-100"><p className="text-[12px] text-gray-500">Konu</p><p className="text-[14px] font-medium text-[#2b2b2b]">{currentTopic?.stem}</p></div>
                <div className="bg-white rounded-[12px] p-3 border border-gray-100"><p className="text-[12px] text-gray-500">Test</p><p className="text-[14px] font-medium text-[#2b2b2b]">{displayBookSectionLabel(currentExercise)}</p></div>
              </div>
              <div className="flex-1 space-y-3">
                <div className="bg-white rounded-[16px] p-4 border border-gray-100 text-center">
                  <p className="text-[13px] text-gray-500">Geçirilen Süre</p>
                  <p className="text-[24px] font-bold text-[#2b2b2b]">
                    {currentExercise.latest_attempt.time_spent_seconds != null
                      ? `${Math.round(currentExercise.latest_attempt.time_spent_seconds / 60)} Dakika ${currentExercise.latest_attempt.time_spent_seconds % 60} Saniye`
                      : '—'}
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1 bg-white rounded-[16px] p-3 border border-gray-100 text-center">
                    <p className="text-[12px] text-gray-500">Toplam Soru</p>
                    <p className="text-[20px] font-bold">{currentExercise.latest_attempt.score_total}</p>
                  </div>
                  <div className="flex-1 bg-white rounded-[16px] p-3 border-2 border-[#59e56e] text-center">
                    <p className="text-[12px] text-[#59e56e]">Doğru</p>
                    <p className="text-[20px] font-bold text-[#59e56e]">{currentExercise.latest_attempt.score_correct}</p>
                  </div>
                  <div className="flex-1 bg-white rounded-[16px] p-3 border-2 border-[#e53e3e] text-center">
                    <p className="text-[12px] text-[#e53e3e]">Yanlış</p>
                    <p className="text-[20px] font-bold text-[#e53e3e]">
                      {currentExercise.latest_attempt.score_total - currentExercise.latest_attempt.score_correct}
                    </p>
                  </div>
                </div>
                <div className="bg-[#3973bf] rounded-[16px] p-4 text-center">
                  <p className="text-[13px] text-white">Başarı</p>
                  <p className="text-[28px] font-bold text-white">
                    %{currentExercise.latest_attempt.score_total > 0
                      ? Math.round((currentExercise.latest_attempt.score_correct / currentExercise.latest_attempt.score_total) * 100)
                      : 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setModal(null)} className="bg-white text-[#2b2b2b] text-[14px] font-semibold px-6 py-3 rounded-[12px] border border-gray-200">Kitaba Geri Dön</button>
              {/* B2: "Video Çözümleri" artık cevap incelemesine (cevaplar) bağlı —
                  soru cevap anahtarı + çözüm videosu o görünümde soru bazında birlikte. */}
              <button onClick={() => setModal('cevaplar')} className="bg-[#e5599b] text-white text-[14px] font-semibold px-6 py-3 rounded-[12px]">Video Çözümleri</button>
            </div>
          </div>
        </div>
      )}

      {/* Cevapları Gör — question image + student's pick vs correct, per-question (gated) */}
      {modal === 'cevaplar' && currentExercise && currentExercise.answers && (() => {
        const correctCount = currentExercise.answers.filter((a) => a.is_correct).length;
        const total = currentExercise.answers.length;
        const currentAnswer = currentExercise.answers.find((a) => a.question_number === testCurrentQ);
        const currentQ = currentExercise.questions.find((x) => x.question_number === testCurrentQ);
        const navNums = currentExercise.question_numbers;
        const navIdx = navNums.indexOf(testCurrentQ);
        const navPrev = navIdx > 0 ? navNums[navIdx - 1] : null;
        const navNext = navIdx >= 0 && navIdx < navNums.length - 1 ? navNums[navIdx + 1] : null;
        return (
          <div
            className="fixed inset-0 z-50 bg-white flex flex-col"
            onContextMenu={(e) => e.preventDefault()}
            style={{ userSelect: 'none' }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <p className="text-[16px] font-bold text-[#2b2b2b]">{displayBookSectionLabel(currentExercise)}</p>
                <p className="text-[13px] text-gray-500">Cevap Anahtarı — {correctCount} / {total} doğru — Soru {testCurrentQ}</p>
              </div>
              <button onClick={() => setModal(null)} className="bg-white text-[#2b2b2b] text-[13px] font-semibold px-5 py-2.5 rounded-[12px] border border-gray-200">Kitaba Geri Dön</button>
            </div>
            <div className="flex-1 flex overflow-hidden">
              {/* Question image */}
              <div className="flex-1 relative">
                <div className="absolute inset-0 bg-[#f6f6f6] p-4 overflow-auto flex items-start justify-center">
                  {currentQ?.image_url ? (
                    <img
                      src={currentQ.image_url}
                      alt={`Soru ${testCurrentQ}`}
                      draggable={false}
                      className="max-w-full bg-white border border-gray-200 rounded-[12px]"
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  ) : currentQ?.cover_type === 'text' && currentQ.cover_text ? (
                    // P3 new-model text-cover question — no image, the body IS text.
                    <div className="max-w-[640px] w-full bg-white border border-gray-200 rounded-[12px] p-5 text-[15px] text-[#2b2b2b] whitespace-pre-wrap">
                      {currentQ.pre_text && <p className="font-semibold mb-3">{currentQ.pre_text}</p>}
                      {currentQ.cover_text}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-[14px] mt-10">Bu soru için görsel bulunamadı.</p>
                  )}
                </div>
                {/* Prev / next arrows flanking the question — vertical blue rectangles
                    matching the test runner. Prev far left, next on the right edge
                    (just left of the answer/video panel). */}
                <button
                  onClick={() => navPrev != null && setTestCurrentQ(navPrev)}
                  disabled={navPrev == null}
                  aria-label="Önceki soru"
                  className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 w-[52px] h-[104px] rounded-[16px] flex items-center justify-center shadow-lg transition-colors ${
                    navPrev == null ? 'bg-white text-gray-300 border border-gray-200 cursor-not-allowed shadow-sm' : 'bg-[#3973bf] text-white hover:bg-blue-700'
                  }`}
                >
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
                <button
                  onClick={() => navNext != null && setTestCurrentQ(navNext)}
                  disabled={navNext == null}
                  aria-label="Sonraki soru"
                  className={`absolute right-3 top-1/2 -translate-y-1/2 z-10 w-[52px] h-[104px] rounded-[16px] flex items-center justify-center shadow-lg transition-colors ${
                    navNext == null ? 'bg-white text-gray-300 border border-gray-200 cursor-not-allowed shadow-sm' : 'bg-[#3973bf] text-white hover:bg-blue-700'
                  }`}
                >
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              </div>
              {/* Answer review */}
              <div className="w-[45%] flex flex-col border-l">
                <div className="flex-1 p-5 overflow-auto">
                  {(() => {
                    const hasCaseDetail = !!currentAnswer?.detail?.cases?.length;
                    const answeredViaCases = hasCaseDetail && currentAnswer!.detail!.cases!.some((c) => c.selected_option_id != null || c.selected_bool != null);
                    const blank = currentAnswer?.selected_option == null && !answeredViaCases;
                    const correct = !!currentAnswer?.is_correct;
                    // Durum-type answers show their per-case verdicts below —
                    // the single-letter card would only display "—".
                    if (hasCaseDetail) {
                      const earned = currentAnswer?.points_earned ?? 0;
                      const totalP = currentAnswer?.points_total ?? 0;
                      return (
                        <div className={`rounded-[16px] p-4 mb-4 border-2 ${correct ? 'border-[#59e56e] bg-[#eafdf0]' : blank ? 'border-[#f5c518] bg-[#fff8e1]' : 'border-[#e53e3e] bg-[#fee2e2]'}`}>
                          <p className="text-[13px] font-semibold" style={{ color: correct ? '#3aa055' : blank ? '#a97e00' : '#c53030' }}>
                            {correct ? '✓ Tümü doğru' : blank ? '○ Boş bırakıldı' : `Kısmen doğru — ${earned}/${totalP} puan`}
                          </p>
                        </div>
                      );
                    }
                    const frameBorder = correct ? 'border-[#59e56e]' : blank ? 'border-[#f5c518]' : 'border-[#e53e3e]';
                    const frameBg = correct ? 'bg-[#eafdf0]' : blank ? 'bg-[#fff8e1]' : 'bg-[#fee2e2]';
                    const headerColor = correct ? '#3aa055' : blank ? '#a97e00' : '#c53030';
                    const selectedColor = correct ? '#59e56e' : blank ? '#a97e00' : '#e53e3e';
                    const header = correct ? '✓ Doğru cevap' : blank ? '○ Boş bırakıldı' : '✗ Yanlış cevap';
                    return (
                  <div className={`rounded-[16px] p-4 mb-4 border-2 ${frameBorder} ${frameBg}`}>
                    <p className="text-[13px] font-semibold mb-2" style={{ color: headerColor }}>
                      {header}
                    </p>
                    <div className="flex gap-6">
                      <div className="flex-1">
                        <p className="text-[11px] text-gray-500 mb-1">Senin cevabın</p>
                        <p className="text-[28px] font-bold" style={{ color: selectedColor }}>
                          {currentAnswer?.selected_option ?? '—'}
                        </p>
                      </div>
                      <div className="flex-1">
                        <p className="text-[11px] text-gray-500 mb-1">Doğru cevap</p>
                        <p className="text-[28px] font-bold text-[#59e56e]">{currentAnswer?.correct_option}</p>
                      </div>
                    </div>
                  </div>
                    );
                  })()}
                  {/* Option list with highlighting. Durum-type questions (P3
                      new model) have no letters — render per-case verdicts
                      from the attempt's detail instead. */}
                  {currentAnswer?.detail?.cases?.length ? (
                    <div className="flex flex-col gap-2">
                      {(currentQ?.cases ?? []).map((c, ci) => {
                        const d = currentAnswer.detail!.cases!.find((x) => x.case_id === c.id);
                        const optText = (id?: number | null) => (c.options ?? []).find((o) => o.id === id)?.text ?? '—';
                        return (
                          <div key={c.id} className={`rounded-[12px] border-2 p-3 ${d?.is_correct ? 'border-[#59e56e] bg-[#eafdf0]' : 'border-[#e53e3e] bg-[#fee2e2]'}`}>
                            <p className="text-[14px] text-[#2b2b2b] mb-1">
                              <span className="font-bold mr-2">{ci + 1}.</span>
                              {c.statement_text ?? (
                                <>{c.before_text} <b>___</b> {c.after_text}</>
                              )}
                            </p>
                            <p className="text-[13px] text-gray-700">
                              {d?.correct_bool !== undefined ? (
                                <>Cevabın: <b>{d?.selected_bool == null ? 'Boş' : d.selected_bool ? 'Doğru' : 'Yanlış'}</b> · Doğrusu: <b className="text-[#3aa055]">{d?.correct_bool ? 'Doğru' : 'Yanlış'}</b></>
                              ) : (
                                <>Cevabın: <b>{d?.selected_option_id == null ? 'Boş' : optText(d?.selected_option_id)}</b> · Doğrusu: <b className="text-[#3aa055]">{optText(d?.correct_option_id)}</b></>
                              )}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                  <div className="flex gap-2">
                    {OPTION_LETTERS.slice(0, currentExercise.max_options).map((letter) => {
                      const isCorrect = letter === currentAnswer?.correct_option;
                      const isSelected = letter === currentAnswer?.selected_option;
                      return (
                        <div
                          key={letter}
                          className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-[12px] border ${
                            isCorrect ? 'border-[#59e56e] bg-[#eafdf0]' : isSelected ? 'border-[#e53e3e] bg-[#fee2e2]' : 'border-gray-200'
                          }`}
                        >
                          <div
                            className="w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center"
                            style={{ borderColor: isCorrect ? '#59e56e' : isSelected ? '#e53e3e' : '#d1d5db', background: isCorrect || isSelected ? (isCorrect ? '#59e56e' : '#e53e3e') : 'transparent' }}
                          >
                            {(isCorrect || isSelected) && (
                              <svg width="10" height="8" viewBox="0 0 10 8" fill="white" stroke="white" strokeWidth="1.5">
                                <path d="M1 4l3 3 5-6" fill="none" />
                              </svg>
                            )}
                          </div>
                          <span className="text-[15px] font-semibold text-[#2b2b2b]">{letter}</span>
                        </div>
                      );
                    })}
                  </div>
                  )}

                  {/* L-3: solution video is shown inline with the answer key,
                      not as a separate modal — student and teacher should see
                      both together in one flow. */}
                  <div className="mt-5">
                    <p className="text-[13px] font-semibold text-[#2b2b2b] mb-2">Soru Çözüm Videosu</p>
                    {currentQ?.video_url ? (
                      <VideoWithSpeed
                        src={currentQ.video_url}
                        className="w-full rounded-[12px] bg-black"
                      />
                    ) : (
                      <div className="bg-[#f6f6f6] border border-gray-200 rounded-[12px] p-4 text-[13px] text-gray-500 text-center">
                        Bu soru için çözüm videosu henüz yok.
                      </div>
                    )}
                  </div>
                </div>
                {/* Question navigator */}
                <div className="p-3 border-t bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[13px] font-semibold text-[#2b2b2b]">Sorular</p>
                    {(() => {
                      const nums = currentExercise.question_numbers;
                      const idx = nums.indexOf(testCurrentQ);
                      const prev = idx > 0 ? nums[idx - 1] : null;
                      const next = idx >= 0 && idx < nums.length - 1 ? nums[idx + 1] : null;
                      return (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => prev != null && setTestCurrentQ(prev)}
                            disabled={prev == null}
                            aria-label="Önceki soru"
                            className="w-[36px] h-[32px] rounded-[8px] text-[16px] font-bold bg-gray-100 text-[#2b2b2b] hover:bg-gray-200 disabled:opacity-40"
                          >‹</button>
                          <span className="text-[12px] text-gray-500 min-w-[44px] text-center">{idx + 1}/{nums.length}</span>
                          <button
                            onClick={() => next != null && setTestCurrentQ(next)}
                            disabled={next == null}
                            aria-label="Sonraki soru"
                            className="w-[36px] h-[32px] rounded-[8px] text-[16px] font-bold bg-gray-100 text-[#2b2b2b] hover:bg-gray-200 disabled:opacity-40"
                          >›</button>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {currentExercise.question_numbers.map((n) => {
                      const a = currentExercise.answers!.find((x) => x.question_number === n);
                      const cls = testCurrentQ === n
                        ? 'bg-[#3973bf] text-white ring-2 ring-offset-1 ring-[#3973bf]'
                        : a?.is_correct
                        ? 'bg-[#59e56e] text-white'
                        : a?.selected_option == null
                        ? 'bg-[#f5c518] text-white'
                        : 'bg-[#e53e3e] text-white';
                      return (
                        <button
                          key={n}
                          onClick={() => setTestCurrentQ(n)}
                          className={`w-[32px] h-[32px] rounded-[8px] text-[12px] font-bold ${cls}`}
                        >{n}</button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Soru Çözüm Videoları — question image + solution video per question */}
      {modal === 'soru-cozumu' && currentExercise && (
        <div
          className="fixed inset-0 z-50 bg-white flex flex-col"
          onContextMenu={(e) => e.preventDefault()}
          style={{ userSelect: 'none' }}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div>
              <p className="text-[16px] font-bold text-[#2b2b2b]">{displayBookSectionLabel(currentExercise)}</p>
              <p className="text-[13px] text-gray-500">Soru Çözümü — Soru {testCurrentQ}</p>
            </div>
            <button onClick={() => setModal(null)} className="bg-white text-[#2b2b2b] text-[13px] font-semibold px-5 py-2.5 rounded-[12px] border border-gray-200">Kitaba Geri Dön</button>
          </div>
          {!isTeacher && !currentExercise.latest_attempt && (
            <div className="px-6 py-3 bg-[#fdecef] border-b border-[#e5599b] flex items-center gap-3">
              <span className="w-[24px] h-[24px] rounded-full bg-[#e5599b] flex items-center justify-center text-white text-[14px] font-bold flex-shrink-0">!</span>
              <p className="text-[13px] text-[#2b2b2b]">
                <strong>Bu bölümün testini henüz çözmedin.</strong> Videoları şimdi izlersen, testini sonradan çözsen bile cevapların <strong>boş kabul edilecek</strong> ve performansına 0 puan olarak işlenecek.
              </p>
            </div>
          )}
          <div className="flex-1 flex overflow-hidden">
            {(() => {
              const q = currentExercise.questions.find((x) => x.question_number === testCurrentQ);
              const navNums = currentExercise.question_numbers;
              const navIdx = navNums.indexOf(testCurrentQ);
              const navPrev = navIdx > 0 ? navNums[navIdx - 1] : null;
              const navNext = navIdx >= 0 && navIdx < navNums.length - 1 ? navNums[navIdx + 1] : null;
              return (
                <>
                  <div className="flex-1 relative">
                    <div className="absolute inset-0 bg-[#f6f6f6] p-4 overflow-auto flex items-start justify-center">
                      {q?.image_url ? (
                        <img
                          src={q.image_url}
                          alt={`Soru ${testCurrentQ}`}
                          draggable={false}
                          className="max-w-full bg-white border border-gray-200 rounded-[12px]"
                          onContextMenu={(e) => e.preventDefault()}
                          onDragStart={(e) => e.preventDefault()}
                        />
                      ) : (
                        <p className="text-gray-400 text-[14px] mt-10">Bu soru için görsel bulunamadı.</p>
                      )}
                    </div>
                    {/* Prev / next arrows flanking the question — vertical blue rectangles
                        matching the test runner. Prev far left, next on the right edge
                        (just left of the solution video). */}
                    <button
                      onClick={() => navPrev != null && setTestCurrentQ(navPrev)}
                      disabled={navPrev == null}
                      aria-label="Önceki soru"
                      className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 w-[52px] h-[104px] rounded-[16px] flex items-center justify-center shadow-lg transition-colors ${
                        navPrev == null ? 'bg-white text-gray-300 border border-gray-200 cursor-not-allowed shadow-sm' : 'bg-[#3973bf] text-white hover:bg-blue-700'
                      }`}
                    >
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                    </button>
                    <button
                      onClick={() => navNext != null && setTestCurrentQ(navNext)}
                      disabled={navNext == null}
                      aria-label="Sonraki soru"
                      className={`absolute right-3 top-1/2 -translate-y-1/2 z-10 w-[52px] h-[104px] rounded-[16px] flex items-center justify-center shadow-lg transition-colors ${
                        navNext == null ? 'bg-white text-gray-300 border border-gray-200 cursor-not-allowed shadow-sm' : 'bg-[#3973bf] text-white hover:bg-blue-700'
                      }`}
                    >
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                  </div>
                  <div className="w-[45%] flex flex-col border-l">
                    {/* Teacher-only: answer key shown above the solution video so the
                        teacher sees the question, the correct answer and the video
                        together in one read-only flow. */}
                    {isTeacher && (q as any)?.correct_option && (
                      <div className="px-4 py-3 bg-[#eafdf0] border-b border-[#59e56e] flex items-center gap-3">
                        <span className="text-[13px] font-semibold text-[#3aa055]">Cevap Anahtarı</span>
                        <span className="text-[20px] font-bold text-[#59e56e]">{(q as any).correct_option}</span>
                      </div>
                    )}
                    <div className="flex-1 bg-black flex items-center justify-center p-4">
                      {q?.video_url ? (
                        <VideoWithSpeed
                          src={q.video_url}
                          wrapperClassName="w-full flex flex-col items-center gap-1"
                          className="max-w-full max-h-[60vh] rounded-[8px]"
                        />
                      ) : (
                        <p className="text-white text-[14px]">Bu soru için çözüm videosu henüz yok.</p>
                      )}
                    </div>
                    <div className="p-3 border-t bg-white">
                      {/* Soru navigasyon — < ve > butonları (#23). Sıralı geçişi
                          hızlandırırken eski numara grid'i (atlamalı seçim için)
                          aşağıda korunuyor. */}
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[13px] font-semibold text-[#2b2b2b]">Sorular</p>
                        {(() => {
                          const nums = currentExercise.question_numbers;
                          const idx = nums.indexOf(testCurrentQ);
                          const prev = idx > 0 ? nums[idx - 1] : null;
                          const next = idx >= 0 && idx < nums.length - 1 ? nums[idx + 1] : null;
                          return (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => prev != null && setTestCurrentQ(prev)}
                                disabled={prev == null}
                                aria-label="Önceki soru"
                                className="w-[36px] h-[32px] rounded-[8px] text-[16px] font-bold bg-gray-100 text-[#2b2b2b] hover:bg-gray-200 disabled:opacity-40 disabled:hover:bg-gray-100"
                              >‹</button>
                              <span className="text-[12px] text-gray-500 min-w-[44px] text-center">{idx + 1}/{nums.length}</span>
                              <button
                                onClick={() => next != null && setTestCurrentQ(next)}
                                disabled={next == null}
                                aria-label="Sonraki soru"
                                className="w-[36px] h-[32px] rounded-[8px] text-[16px] font-bold bg-gray-100 text-[#2b2b2b] hover:bg-gray-200 disabled:opacity-40 disabled:hover:bg-gray-100"
                              >›</button>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {currentExercise.question_numbers.map((n) => (
                          <button
                            key={n}
                            onClick={() => setTestCurrentQ(n)}
                            className={`w-[32px] h-[32px] rounded-[8px] text-[12px] font-bold ${
                              testCurrentQ === n ? 'bg-[#3973bf] text-white' : 'bg-gray-100 text-[#2b2b2b] hover:bg-gray-200'
                            }`}
                          >{n}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Ders Videosu — the lesson's teaching video */}
      {modal === 'ders-videosu' && currentExercise?.lesson_video_url && (
        <div className="fixed inset-0 z-50 bg-black/85 flex flex-col" onClick={() => setModal(null)}>
          <div className="flex items-center justify-between px-6 py-3">
            <p className="text-white text-[15px] font-semibold truncate">{displayBookSectionLabel(currentExercise)} — Ders Videosu</p>
            <button onClick={() => setModal(null)} className="bg-white/15 hover:bg-white/25 text-white text-[13px] font-semibold px-4 py-2 rounded-[8px]">Kapat</button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-full max-w-[960px]">
              <VideoWithSpeed src={currentExercise.lesson_video_url} className="w-full rounded-[12px] bg-black" />
            </div>
          </div>
        </div>
      )}

      {/* İçerik videosu — a node-attached lecture/solution video (book_node_contents) */}
      {contentVideo && (
        <div className="fixed inset-0 z-50 bg-black/85 flex flex-col" onClick={() => setContentVideo(null)}>
          <div className="flex items-center justify-between px-6 py-3">
            <p className="text-white text-[15px] font-semibold truncate">{contentVideo.title}</p>
            <button onClick={() => setContentVideo(null)} className="bg-white/15 hover:bg-white/25 text-white text-[13px] font-semibold px-4 py-2 rounded-[8px]">Kapat</button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-full max-w-[960px]">
              <VideoWithSpeed src={contentVideo.url} className="w-full rounded-[12px] bg-black" />
            </div>
          </div>
        </div>
      )}

      {/* PDF View — all pages, shared drawing toolbar, per-section annotations */}
      {(modal === 'pdf' || modal === 'pdf-ca' || modal === 'bookpdf') && (() => {
        const sectionPdfUrl = modal === 'pdf' ? currentExercise?.pdf_url : null;
        const activeItem = modal === 'pdf-ca'
          ? caItem
          : modal === 'bookpdf'
            ? (book.pdf_url ? { signedUrl: book.pdf_url, title: book.display_name ?? book.title } : null)
            : sectionPdfUrl ? { signedUrl: sectionPdfUrl, title: currentExercise ? displayBookSectionLabel(currentExercise) : 'PDF' } : pdfItem;
        // Alt başlık mode always uses the narrowed per-page set. Whole-book
        // mode uses one verified catalog when available; only an unverified or
        // absent catalog falls back to every split page.
        const useSplitPages = modal === 'bookpdf' && book.has_page_pdfs
          && !(pdfNode == null && book.pdf_scope_verified && book.pdf_url);
        const digipoPages = useSplitPages ? (bookPages ?? []) : undefined;
        if (!activeItem?.signedUrl && !digipoPages) return null;
        // A slice is on screen when the server narrowed the response to a node's
        // printed pages. page_start/page_end are null for a whole-book response.
        const isSlice =
          modal === 'bookpdf' && !!digipoPages && !!pageSlice && pageSlice.start != null && pageSlice.end != null;
        const renderWidth = Math.round((pdfWidth - 16) * pdfScale);
        // Determine annotation key for this PDF session (per user+book+section).
        // The whole-book PDF gets its own 'bookpdf' key so its drawings don't
        // collide with any per-section annotations.
        const annKey = modal === 'pdf'
          ? (currentExercise?.section_label ?? 'main')
          : modal === 'bookpdf' ? 'bookpdf' : 'ca';
        return (
          <div
            className="fixed inset-0 z-50 bg-white flex flex-col"
            onContextMenu={(e) => e.preventDefault()}
            style={{ userSelect: 'none' }}
          >
            <div className="flex items-center justify-between px-6 py-3 border-b bg-white z-10">
              <div className="flex items-center gap-3">
                {/* L-5: back / "go back" button in the top-left of the PDF viewer. */}
                <button
                  onClick={() => setModal(null)}
                  aria-label="Geri"
                  className="w-[36px] h-[36px] rounded-full hover:bg-gray-100 flex items-center justify-center"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 6l-6 6 6 6" />
                  </svg>
                </button>
                <div>
                  <p className="text-[15px] font-bold text-[#2b2b2b]">
                    {book.display_name ?? book.title}
                    {isSlice && pdfNode?.label ? <span className="font-medium text-[#888]"> · {pdfNode.label}</span> : null}
                  </p>
                  {(modal === 'pdf-ca' || pdfNumPages > 0) && (
                    <p className="text-[13px] text-[#2b2b2b]">
                      {modal === 'pdf-ca' ? 'Cevap Anahtarı' : ''}
                      {modal === 'pdf-ca' && pdfNumPages > 0 ? ' — ' : ''}
                      {isSlice
                        ? `Sayfa ${pageSlice!.start}-${pageSlice!.end} (${pdfNumPages} sayfa) · kitap ${pageSlice!.count} sayfa`
                        : pdfNumPages > 0 && `${pdfNumPages} Sayfa`}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Escape hatch: a lesson range is a convenience, never a cage.
                    Also the fallback when a range is wrong or too narrow. */}
                {isSlice && (
                  <button
                    onClick={() => setPdfNode(null)}
                    className="text-[13px] font-semibold px-3 py-2 rounded-[8px] border border-gray-200 text-[#2b2b2b] hover:bg-gray-100"
                  >
                    Tüm kitabı aç
                  </button>
                )}
                <div className="flex items-center bg-gray-100 rounded-[8px]">
                  <button onClick={() => setPdfScale((s) => Math.max(0.5, +(s - 0.1).toFixed(2)))} disabled={pdfScale <= 0.5}
                    className="w-[32px] h-[32px] flex items-center justify-center hover:bg-gray-200 rounded-l-[8px] text-[16px] font-semibold disabled:opacity-30">−</button>
                  <button onClick={() => setPdfScale(1.0)}
                    className="min-w-[56px] h-[32px] px-2 text-[13px] font-semibold hover:bg-gray-200">{Math.round(pdfScale * 100)}%</button>
                  <button onClick={() => setPdfScale((s) => Math.min(2.5, +(s + 0.1).toFixed(2)))} disabled={pdfScale >= 2.5}
                    className="w-[32px] h-[32px] flex items-center justify-center hover:bg-gray-200 rounded-r-[8px] text-[16px] font-semibold disabled:opacity-30">+</button>
                </div>
                <button onClick={() => setModal(null)} className="ml-2 bg-[#3973bf] text-white text-[13px] font-semibold px-4 py-2 rounded-[8px]">Kitaba Geri Dön</button>
              </div>
            </div>

            {/* Single drawing toolbar for all pages */}
            {(modal === 'pdf' || modal === 'bookpdf') && (
              <DrawToolbar
                toolState={drawToolState}
                onToolStateChange={setDrawToolState}
                onUndo={handleAnnotationUndo}
                onRedo={handleAnnotationRedo}
                onClear={handleAnnotationClear}
                canUndo={annHistoryIdx > 0}
                canRedo={annHistoryIdx < annHistory.length - 1}
              />
            )}

            <div ref={pdfContainerRef} className="flex-1 overflow-auto bg-[#f6f6f6] flex flex-col items-center p-4 gap-3">
              <PdfLoader
                file={activeItem?.signedUrl ?? ''}
                pages={digipoPages}
                annKey={annKey}
                renderWidth={renderWidth}
                pdfScale={pdfScale}
                showCanvas={modal === 'pdf' || modal === 'bookpdf'}
                annotations={annotations}
                drawToolState={drawToolState}
                onAnnotationsChange={handleAnnotationsChange}
                onNumPages={setPdfNumPages}
                onLoadAnnotations={loadAnnotationsForSection}
                currentAnnotationKey={currentAnnotationKey}
              />
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default BookDetailView;
